import { identity, isMatch, pickBy } from "lodash";

/**
 * Categorizes any query that should be invalidated when the user auth
 * state is changed.
 *
 * For example, if a query returns different data based on the user that is logged in -
 * this ensures that the cache will not leak between sessions on the same browser.
 */
export const QUERY_KEY_AUTH = "auth";

/**
 * Categorizes any query that should be invalidated when any artist is changed,
 * regardless of the artistId/artistSlug.
 */
export const QUERY_KEY_ARTISTS = "artists";

/**
 * Categorizes any query that should be invalidated when any trackgroup is changed,
 * regardless of the trackGroupId.
 */
export const QUERY_KEY_TRACK_GROUPS = "trackGroups";

export const QUERY_KEY_TRACKS = "tracks";

/**
 * Categorizes any query that should be invalidated when any merch is changed,
 * regardless of the merchId.
 */
export const QUERY_KEY_MERCH = "merch";

/**
 * Categorizes any query that should be invalidated when any tag is changed,
 * regardless of the tagId.
 */
export const QUERY_KEY_TAGS = "tags";

/**
 * Categorizes any query that should be invalidated when any post is changed,
 * regardless of the postId.
 */
export const QUERY_KEY_POSTS = "posts";

/**
 * Categorizes any query that should be invalidated when any purchase is changed,
 * regardless of the purchaseId.
 */
export const QUERY_KEY_PURCHASES = "purchases";

/**
 * Sales aren't stored in the database by id, but a are a combination of merch,
 * tracks, trackGroup, and tip purchases
 */
export const QUERY_KEY_SALES = "sales";

/**
 * Categorizes any query that should be invalidated when any notification is changed,
 * regardless of the notificationId.
 */
export const QUERY_KEY_NOTIFICATIONS = "notifications";

/**
 * Categorizes any query that should be invalidated when any notification is changed,
 * regardless of the notificationId.
 */
export const QUERY_KEY_LABELS = "labels";

/**
 * Categorizes queries about who manages an artist, and the logged in user's
 * pending invites to manage artists.
 */
export const QUERY_KEY_ARTIST_MANAGERS = "artistManagers";

export type QueryTag =
  | typeof QUERY_KEY_AUTH
  | typeof QUERY_KEY_ARTISTS
  | typeof QUERY_KEY_TRACK_GROUPS
  | typeof QUERY_KEY_TRACKS
  | typeof QUERY_KEY_MERCH
  | typeof QUERY_KEY_PURCHASES
  | typeof QUERY_KEY_SALES
  | typeof QUERY_KEY_TAGS
  | typeof QUERY_KEY_POSTS
  | typeof QUERY_KEY_NOTIFICATIONS
  | typeof QUERY_KEY_LABELS
  | typeof QUERY_KEY_ARTIST_MANAGERS;

export type QueryArgs = {
  artistId: number;
  artistSlug: string;
  merchId: string;
};

/**
 * Checks whether the arguments in a given query key contains
 * ALL of the provided args.
 */
export function queryKeyMatches(
  query: { queryKey: unknown },
  args: Partial<QueryArgs>
): boolean {
  if (query.queryKey instanceof Array) {
    const queryArgs = query.queryKey[1];
    if (queryArgs && typeof queryArgs === "object") {
      return isMatch(pickBy(queryArgs, identity), pickBy(args, identity));
    }
  }

  return false;
}

/**
 * Checks whether a given query key includes ALL of the provided tags
 */
export function queryKeyIncludes(
  query: { queryKey: unknown },
  ...tags: QueryTag[]
): boolean {
  if (query.queryKey instanceof Array) {
    const key = query.queryKey;
    return tags.every((tag) => key.includes(tag));
  }

  return false;
}
